import { Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import { CacheService, Card, CardService, Company, CompanyService, Funnel, FunnelService, Pager, Stage, StageService, User, UserService } from "lib-trend-core";
import { of, Subject } from "rxjs";
import { catchError, concatMap, exhaustMap, map, mergeMap, switchMap, take, takeUntil } from "rxjs/operators";
import * as CRMActions from "./actions";
import { stagesSelector } from "./selectors";

@Injectable()
export class CRMEffects {
  private destroy$ = new Subject<void>();

  constructor(
    private store: Store,
    private actions$: Actions,
    private cacheService: CacheService,
    private funnelService: FunnelService,
    private stageService: StageService,
    private cardService: CardService,
    private companyService: CompanyService,
    private userService: UserService,
  ) { }

  ngOnInit(): void {
    this.store.dispatch(CRMActions.clearFullState({ clear: true }));
  }

  ngOnDestroy(): void {
    this.destroyEffects();
  }

  destroyEffects(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadInitCompanyEffect$ = createEffect(() => {
    let userSelector: User = undefined;
    return this.actions$.pipe(
      ofType(CRMActions.getLoadInit),
      exhaustMap((action) => {
        return this.userService.getById(action.idUser)
          .pipe(
            takeUntil(this.destroy$),
            mergeMap((user: User) => {
              userSelector = user;
              return this.companyService.getById(action.idCompany);
            }),
            map((company: Company) => {
              return CRMActions.setLoadInit({ user: userSelector, company: company });
            }),
            catchError((error) => of(CRMActions.setFailure({ error: error.message }))),
          );
      })
    );
  });

  funnelsEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(CRMActions.getFunnels),
      switchMap((action) => {
        return this.funnelService.getList().pipe(
          takeUntil(this.destroy$),
          map((pager: Array<Funnel>) => {
            return CRMActions.setFunnels({ funnels: pager });
          }),
          catchError((error) => of(CRMActions.setFailure({ error: error.message }))),
        )
      })
    );
  });

  funnelEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(CRMActions.getFunnel),
      exhaustMap((action) => {
        return this.funnelService.getById(action.idFunnel)
          .pipe(
            takeUntil(this.destroy$),
            map((funnel: Funnel) => {
              return CRMActions.setFunnel({ funnel });
            }),
            catchError((error) => of(CRMActions.setFailure({ error: error.message }))),
          );
      })
    );
  });

  stagesEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(CRMActions.getStages),
      exhaustMap((action) => {
        return this.stageService.getStagesByFunnel(action.idFunnel)
          .pipe(
            takeUntil(this.destroy$),
            map((stages: Array<Stage>) => CRMActions.setStages({ stages })),
            catchError((error) => of(CRMActions.setFailure({ error: error.message }))),
          )
      })
    );
  });

  stageEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(CRMActions.getStage),
      concatMap((action) => {
        return this.stageService.getById(action.idStage).pipe(
          takeUntil(this.destroy$),
          switchMap((stage: Stage) => {
            return this.store.select(stagesSelector).pipe(
              take(1),
              map(currentStages => {
                const updatedStages = currentStages.map(s =>
                  s._id === stage._id ? stage : s
                );
                return CRMActions.setStages({ stages: updatedStages });
              })
            );
          }),
          catchError((error) => of(CRMActions.setFailure({ error: error.message })))
        );
      })
    );
  });

  cardsEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(CRMActions.getCards),
      exhaustMap((action) => {
        return this.cardService.getCardsPaginated(action.page, action.perPage, null, action.params)
          .pipe(
            takeUntil(this.destroy$),
            map((pager: Pager<Card>) => {
              return CRMActions.setCards({ cards: pager });
            }),
            catchError((error) => of(CRMActions.setFailure({ error: error.message }))),
          )
      })
    );
  });

  cardEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(CRMActions.getCard),
      exhaustMap((action) => {
        return this.cardService.getById(action.idCard)
          .pipe(
            takeUntil(this.destroy$),
            map((card: Card) => {
              return CRMActions.setCard({ card });
            }),
            catchError((error) => of(CRMActions.setFailure({ error: error.message }))),
          );
      })
    );
  });

  queryParamsEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(CRMActions.getCards),
      map((action) => CRMActions.setQueryParams({ queryParams: action.params }))
    );
  });

}
